import { useCallback, useEffect, useRef, useState } from 'react';

import i18n from '~/locale';
import type { ConversationReference, ConversationSection } from '~/types/api.graphql';

import { useAIInsightsSidebar } from './AIInsightsSidebarContext';
import {
  clearEditorIfEmpty,
  computeHasContent,
  dismissPendingMention,
  dispatchInputEvent,
  getNodeText,
  getPendingChip,
  getPendingChipFromSelection,
  handleBackspaceAroundInternalMarkers,
  handlePlainArrowAroundChipBoundary,
  insertClipboardText,
  insertInlineMentionReference,
  insertReferenceAtCaret as insertReferenceAtCaretDom,
  isBackspaceDeleteKeyEvent,
  isPlainKeyEvent,
  removeChipFromMouseEvent,
  replacePendingChipWithText,
  scanMentionQuery,
  serializeEditorContent,
  setCaretAtEndOfElement,
  shouldRemovePendingChipFromDelete,
  shouldRestoreCaretAfterPendingChipRemoval,
  wrapPendingChip,
  writeSelectionToClipboard,
} from './mentionInputDom';

const MAX_LINES = 5;
const TYPEWRITER_INTERVAL_MS = 28;
const EDITOR_BLOCK_TAGS = ['DIV', 'P', 'LI', 'BLOCKQUOTE'];

interface UseAIMentionInputOptions {
  section: ConversationSection | null;
  animateTyping?: boolean;
}

interface HandleMentionKeyDownOptions {
  onSubmit: () => void;
}

export const useAIMentionInput = ({ section, animateTyping = false }: UseAIMentionInputOptions) => {
  const { isOpen, clickCounterCTA } = useAIInsightsSidebar();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const savedHTMLRef = useRef('');
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingRunRef = useRef(0);

  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [hasContent, setHasContent] = useState(false);

  const editorCallbackRef = useCallback((el: HTMLDivElement | null) => {
    if (el === null) {
      savedHTMLRef.current = editorRef.current?.innerHTML ?? '';
    } else {
      if (savedHTMLRef.current && savedHTMLRef.current !== '<br>') {
        el.innerHTML = savedHTMLRef.current;
        setHasContent(computeHasContent(el));
      }
      savedHTMLRef.current = '';
    }
    editorRef.current = el;
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return undefined;

    const clampHeight = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
      const maxHeight = lineHeight * MAX_LINES;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
      el.style.maxHeight = `${maxHeight}px`;
    };

    const observer = new ResizeObserver(clampHeight);
    observer.observe(el);
    clampHeight();

    return () => observer.disconnect();
  }, []);

  const clearTypingTimer = useCallback(() => {
    typingRunRef.current += 1;
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (animateTyping) return;
    const key = `aiInsights.prompts.${section as string}`;
    if (section && isOpen && editorRef.current && i18n.exists(key)) {
      const text = i18n.t(key);
      const current = getNodeText(editorRef.current);
      if (!current.includes(text)) {
        const combined = current ? `${current}\n${text}` : text;
        editorRef.current.textContent = combined;
        setHasContent(combined.trim().length > 0);
      }
    }
  }, [isOpen, section, clickCounterCTA, animateTyping]);

  useEffect(() => {
    if (!isOpen) clearTypingTimer();
  }, [isOpen, clearTypingTimer]);

  useEffect(() => () => clearTypingTimer(), [clearTypingTimer]);

  const startTypingPrefill = useCallback(() => {
    clearTypingTimer();

    const key = `aiInsights.prompts.${section as string}`;
    if (!section || !isOpen || !editorRef.current || !i18n.exists(key)) return;

    const text = i18n.t(key);
    if (!text) return;

    // Don't append if the text is already present in the editor.
    if (getNodeText(editorRef.current).includes(text)) return;

    // Append typed text while preserving existing chips and line structure.
    //
    // Browsers represent a user-pressed Enter as a trailing <div><br></div>
    // (or standalone <br>). We type INTO that element rather than removing it
    // and re-appending, which would collapse the line break.
    const lastChild = editorRef.current.lastChild;
    const lastChildIsBlock = lastChild instanceof HTMLElement && EDITOR_BLOCK_TAGS.includes(lastChild.tagName);
    const lastChildIsEmptyBlock = lastChildIsBlock && (lastChild.textContent ?? '').trim() === '';
    const lastChildIsBR = lastChild instanceof HTMLElement && lastChild.tagName === 'BR';

    let appendNode: Text;
    let prefix = '';
    if (lastChildIsEmptyBlock) {
      // Type into the existing empty block (remove the placeholder <br> first).
      lastChild.innerHTML = '';
      appendNode = document.createTextNode('');
      lastChild.appendChild(appendNode);
    } else if (lastChildIsBR) {
      // Replace the standalone <br> with a text node.
      appendNode = document.createTextNode('');
      editorRef.current.replaceChild(appendNode, lastChild);
    } else if (lastChildIsBlock) {
      const block = document.createElement('div');
      appendNode = document.createTextNode('');
      block.appendChild(appendNode);
      editorRef.current.appendChild(block);
    } else {
      // No trailing empty line — prepend \n unless the content already ends with one.
      const existing = getNodeText(editorRef.current);
      prefix = existing && !existing.endsWith('\n') ? '\n' : '';
      appendNode = document.createTextNode('');
      editorRef.current.appendChild(appendNode);
    }

    let index = 1;
    const typingRun = typingRunRef.current;
    const typeChar = () => {
      const editor = editorRef.current;
      if (!editor || typingRunRef.current !== typingRun || !editor.contains(appendNode)) return;

      appendNode.textContent = prefix + text.slice(0, index);
      setHasContent(computeHasContent(editor));
      // Keep caret at the end so it follows the typed characters.
      const sel = window.getSelection();
      if (sel) setCaretAtEndOfElement(sel, editor);
      index++;
      if (index <= text.length) {
        typingTimerRef.current = setTimeout(typeChar, TYPEWRITER_INTERVAL_MS);
      } else {
        typingTimerRef.current = null;
      }
    };

    typeChar();
  }, [section, isOpen, clearTypingTimer]);

  const clearMentionState = useCallback(() => {
    editorRef.current?.removeAttribute('data-mention-pending');
    setMentionQuery(null);
  }, []);

  const finalizeEditorMutation = useCallback(
    (editor: HTMLElement) => {
      clearTypingTimer();
      clearMentionState();
      setHasContent(computeHasContent(editor));
      dispatchInputEvent(editor);
    },
    [clearMentionState, clearTypingTimer],
  );

  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    clearTypingTimer();
    savedHTMLRef.current = editor.innerHTML;
    setHasContent(computeHasContent(editor));

    const result = scanMentionQuery();
    if (result) {
      wrapPendingChip();
      setMentionQuery(result.query);
      return;
    }

    const pendingSpan = getPendingChip(editor);
    if (pendingSpan) {
      replacePendingChipWithText({
        pendingChip: pendingSpan,
        text: getNodeText(pendingSpan),
        restoreCaret: shouldRestoreCaretAfterPendingChipRemoval(editor, pendingSpan),
      });
      clearEditorIfEmpty(editor);
    }

    clearMentionState();
  }, [clearMentionState, clearTypingTimer]);

  const insertMention = useCallback(
    (ref: ConversationReference) => {
      const editor = editorRef.current;
      if (!editor) return;

      if (insertInlineMentionReference(editor, ref)) {
        finalizeEditorMutation(editor);
      }
    },
    [finalizeEditorMutation],
  );

  const insertReferenceAtCaret = useCallback(
    (ref: ConversationReference) => {
      const editor = editorRef.current;
      if (!editor) return;

      if (insertReferenceAtCaretDom(editor, ref)) {
        finalizeEditorMutation(editor);
      }
    },
    [finalizeEditorMutation],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, { onSubmit }: HandleMentionKeyDownOptions) => {
      const isPlainKey = isPlainKeyEvent(e);
      const editor = editorRef.current;
      const pendingChip = getPendingChipFromSelection(window.getSelection());

      if (editor && pendingChip && shouldRemovePendingChipFromDelete(e, pendingChip)) {
        e.preventDefault();
        clearTypingTimer();
        replacePendingChipWithText({
          pendingChip,
          text: '',
          restoreCaret: true,
        });
        clearEditorIfEmpty(editor);
        setHasContent(computeHasContent(editor));
        clearMentionState();
        return;
      }

      if (isBackspaceDeleteKeyEvent(e) && handleBackspaceAroundInternalMarkers(window.getSelection(), editor)) {
        e.preventDefault();
        return;
      }

      if (isPlainKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        const arrowKey: 'ArrowLeft' | 'ArrowRight' = e.key;
        if (handlePlainArrowAroundChipBoundary(arrowKey, window.getSelection(), editor)) {
          e.preventDefault();
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    },
    [clearMentionState, clearTypingTimer],
  );

  const closeMention = useCallback(() => {
    const editor = editorRef.current;
    if (editor) dismissPendingMention(editor);
    clearMentionState();
  }, [clearMentionState]);

  const serialize = useCallback(() => {
    if (!editorRef.current) return '';
    return serializeEditorContent(editorRef.current);
  }, []);

  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    writeSelectionToClipboard(e, editor);
  }, []);

  const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    const range = writeSelectionToClipboard(e, editor);
    if (!range) return;

    range.deleteContents();
    dispatchInputEvent(editor);
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    e.preventDefault();
    if (insertClipboardText(editor, e.clipboardData.getData('text/plain'))) {
      dispatchInputEvent(editor);
    }
  }, []);

  const handleEditorMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    removeChipFromMouseEvent(e, editor);
  }, []);

  const reset = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    clearTypingTimer();
    savedHTMLRef.current = '';
    setHasContent(false);
    clearMentionState();
  }, [clearMentionState, clearTypingTimer]);

  return {
    editorRef,
    editorCallbackRef,
    mentionQuery,
    hasContent,
    handleInput,
    handleKeyDown,
    handleCopy,
    handleCut,
    handlePaste,
    handleEditorMouseDown,
    insertMention,
    insertReferenceAtCaret,
    closeMention,
    serialize,
    reset,
    startTypingPrefill,
  };
};
